package com.evoapps.evoshift.domain.model

import kotlin.math.abs

/** Where a month's [MonthlyResultSide.salesIncome] comes from. */
enum class SalesIncomeSource {
    /** The workplace's commission on the net sales (the owner runs it for someone else). */
    COMMISSION,
    /** The net sales themselves: without a commission the owner keeps the sales. */
    NET_SALES,
    /** Both: the commission changed during the month (days with one, days without). */
    MIXED
}

/**
 * One side (plan, actual or forecast) of a month's result (SH-46), the app's version of the
 * owner's "Wynik Finansowy" sheet. All amounts are net and the labour cost is an estimate.
 *
 * Revenue: what the owner earns: the [salesIncome] (each day's commission on its net sales, or
 * the whole net sales on a day without a commission) plus the fixed monthly revenue and the
 * ledger's revenue items. With a commission the net sales are its base, shown but not revenue.
 * Costs: the employer labour cost of the shifts, the month's fixed costs (copied from the
 * recurring cost rules when the month was created), its one-off expenses and (SH-131) its parts of
 * the owner's shared costs.
 * Result = revenue − costs; margin = result ÷ revenue × 100 (none without revenue).
 */
data class MonthlyResultSide(
    val netSales: Double,
    val commission: Double,
    /** What the owner keeps of the sales: the commission, or the net sales of days without one. */
    val salesIncome: Double,
    val fixedRevenue: Double,
    val labourCost: Double,
    val fixedCosts: Double,
    val oneOffCosts: Double,
    /** SH-87: the month's revenue from the recurring rules (e.g. a sublease). */
    val recurringRevenue: Double = 0.0,
    /** SH-87: the month's one-off revenue (e.g. a catering order). */
    val oneOffRevenue: Double = 0.0,
    /** SH-131: this workplace's parts of the owner's shared costs (accountant, software). */
    val sharedCosts: Double = 0.0
) {
    /** The owner's income: the sales income, the fixed revenue and the ledger's revenue. */
    val revenue: Double get() = salesIncome + fixedRevenue + recurringRevenue + oneOffRevenue
    val costs: Double get() = labourCost + fixedCosts + oneOffCosts + sharedCosts
    val result: Double get() = revenue - costs

    /** The result as a share of the revenue, in percent; null when there is no revenue to share. */
    val marginPercent: Double? get() = if (revenue > 0.0) result / revenue * 100.0 else null

    /** Where [salesIncome] comes from; null when there are neither sales nor commission to tell. */
    val salesIncomeSource: SalesIncomeSource?
        get() = when {
            commission <= 0.0 -> if (salesIncome > 0.0) SalesIncomeSource.NET_SALES else null
            abs(salesIncome - commission) < 0.01 -> SalesIncomeSource.COMMISSION
            else -> SalesIncomeSource.MIXED
        }
}

/**
 * The month's result side by side (SH-46): [plan] (the targets and the planned shifts), [actual]
 * (the days with takings entered and the clocked, or worked-as-planned, shifts so far) and
 * [forecast] (the actual where the takings are in, the plan for the rest of the month). Every
 * item of the month's ledger counts whole in all three (dated items, as in the spreadsheet). The
 * fixed revenue is spread over the month's days (SH-95, #215): the plan and the forecast hold all
 * of it, the actual (SH-147) the shares of the days with takings, as the actual labour % and the
 * daily results count it, so "Actual (to date)" and its labour % agree.
 */
data class MonthlyResult(
    val plan: MonthlyResultSide,
    val actual: MonthlyResultSide,
    val forecast: MonthlyResultSide,
    /** The month's days and those with takings: how much of the month the actual covers. */
    val days: Int,
    val daysWithTakings: Int,
    /** Where the month's sales income comes from (labels the card's line), across its sides. */
    val salesIncomeSource: SalesIncomeSource = SalesIncomeSource.COMMISSION
) {
    /** Actual minus plan of the result: positive is better than planned. */
    val resultDifference: Double get() = actual.result - plan.result

    companion object {
        /**
         * The month's result from its totals ([financials], roster_financials) and its ledger
         * ([costs], rosters/{id}/costs): the ledger tells the rules' items from one-off ones, and
         * (SH-87) costs from revenue. Without a ledger (empty or not readable), the totals' other
         * costs count as fixed costs and their other revenue as recurring revenue.
         */
        fun of(financials: RosterFinancials, costs: List<FixedCost>): MonthlyResult {
            val ledger = costs.takeIf { it.isNotEmpty() }
            // SH-131: the parts of shared costs have a line of their own, never also a fixed cost.
            fun sum(oneOff: Boolean, revenue: Boolean) =
                ledger?.filter { !it.isAllocated && it.isOneOff == oneOff && it.isRevenue == revenue }?.sumOf { it.amount }
            val shared = ledger?.filter { it.isAllocated && !it.isRevenue }?.sumOf { it.amount } ?: 0.0
            fun side(summary: FinancialSummary, toDate: Boolean = false) = MonthlyResultSide(
                netSales = summary.revenue,
                commission = summary.commission,
                salesIncome = summary.salesIncomeAmount,
                fixedRevenue = if (toDate) summary.fixedRevenueToDate else summary.fixedRevenue,
                labourCost = summary.laborCost,
                fixedCosts = sum(oneOff = false, revenue = false) ?: summary.otherCosts,
                oneOffCosts = sum(oneOff = true, revenue = false) ?: 0.0,
                recurringRevenue = sum(oneOff = false, revenue = true) ?: summary.otherRevenue,
                oneOffRevenue = sum(oneOff = true, revenue = true) ?: 0.0,
                sharedCosts = shared
            )
            val sides = listOf(side(financials.planned), side(financials.actual, toDate = true), side(financials.combined))
            val sources = sides.mapNotNull { it.salesIncomeSource }.toSet()
            val source = when {
                sources.size > 1 -> SalesIncomeSource.MIXED
                sources.isNotEmpty() -> sources.single()
                // Nothing sold yet: as the labour % is measured.
                financials.combined.labourBase == LabourBase.SALES -> SalesIncomeSource.NET_SALES
                else -> SalesIncomeSource.COMMISSION
            }
            return MonthlyResult(
                plan = sides[0],
                actual = sides[1],
                forecast = sides[2],
                days = financials.days,
                daysWithTakings = financials.daysWithTakings,
                salesIncomeSource = source
            )
        }
    }
}
